import React from 'react'
import { StatusPill } from './StatusPill'

export const MAX_PROGRESS_ENTRIES = 50

const LEVELS = {
    info: { tone: 'info', icon: 'fa-circle-notch' },
    success: { tone: 'success', icon: 'fa-check-circle' },
    warning: { tone: 'warning', icon: 'fa-exclamation-triangle' },
    error: { tone: 'danger', icon: 'fa-times-circle' }
}

const BRAND_STATES = {
    pending: { tone: 'info', label: 'Registering' },
    ready: { tone: 'success', label: 'Registered' },
    failed: { tone: 'danger', label: 'Registration Failed' }
}

const LOCATION_PROGRESS = {
    pending: { tone: 'info', label: 'Provisioning' },
    failed: { tone: 'danger', label: 'Failed' },
    enabled: { tone: 'success', label: 'Provisioned' },
    disabled: { tone: 'secondary', label: 'Provisioned, Off' },
    dormant: { tone: 'secondary', label: 'Dormant At ShoutAboutUs' }
}

// Newest first, without the duplicates a socket reconnect can replay.
export const addProgressEntry = (entries, entry) => {
    if (!entry || entries.some(existing => existing.id === entry.id)) {
        return entries
    }

    return [entry, ...entries].slice(0, MAX_PROGRESS_ENTRIES)
}

export const locationCounts = (locations) => {
    const counts = { total: 0, done: 0, pending: 0, failed: 0 }

    Object.values(locations || {}).forEach(({ state }) => {
        if (state === 'none') {
            return
        }
        counts.total++
        if (state === 'pending') {
            counts.pending++
        } else if (state === 'failed') {
            counts.failed++
        } else {
            counts.done++
        }
    })

    return counts
}

// Shown while a move to ShoutAboutUs is under way or has left something to look at.
export const hasProgress = ({ brand, locations, entries }) => {
    const counts = locationCounts(locations)

    return entries.length > 0 ||
        ['pending', 'failed'].includes(brand?.state) ||
        counts.pending > 0 ||
        counts.failed > 0
}

const switchStep = ({ switched, brand, readiness, waiting }) => {
    if (switched) {
        return <StatusPill tone="success">{__('On ShoutAboutUs')}</StatusPill>
    }
    if (readiness?.ready) {
        return <StatusPill tone="success">{__('Ready To Switch')}</StatusPill>
    }
    if (brand?.state === 'ready' && waiting > 0) {
        return <StatusPill tone="info">{__('Waiting For :count Locations', { ':count': waiting })}</StatusPill>
    }

    return <StatusPill tone="secondary">{__('Waiting For The Business Group')}</StatusPill>
}

// Users and their review alerts come over from ReviewTrackers in the switch itself, so they read as waiting until then.
const carriedOverStep = (switched, count, label) => {
    if (!switched || count === undefined || count === null) {
        return <StatusPill tone="secondary">{__('Copied At The Switch')}</StatusPill>
    }

    return <StatusPill tone="success">{__(label, { ':count': count })}</StatusPill>
}

const formatTime = (iso) => {
    const date = new Date(iso)

    return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString()
}

export const ProviderProgress = ({ title, brand, locations, entries, businesses, live, providerState, readiness, carriedOver, children }) => {
    const names = Object.fromEntries((businesses || []).map(business => [business.business_id, business.name]))
    const counts = locationCounts(locations)
    const brandState = BRAND_STATES[brand?.state]
    const tracked = Object.entries(locations || {}).filter(([, location]) => location.state !== 'none')
    const switched = providerState === 'shout_about_us'
    const waiting = (readiness?.waiting || []).length

    return (
        <div className="rt-progress" aria-live="polite">
            <div className="rt-progress-header">
                <h3 className="rt-progress-title">{title || __('ShoutAboutUs Migration')}</h3>
                {live
                    ? <StatusPill tone="success"><i className="fas fa-circle rt-live-dot" aria-hidden="true" /> {__('Live')}</StatusPill>
                    : <StatusPill tone="secondary">{__('Not Live, Reload To See Updates')}</StatusPill>
                }
            </div>

            <ol className="rt-progress-steps">
                <li className="rt-progress-step">
                    <span className="rt-progress-step-label">{__('Business Group')}</span>
                    {brandState
                        ? <StatusPill tone={brandState.tone}>{__(brandState.label)}</StatusPill>
                        : <StatusPill tone="secondary">{__('Not Started')}</StatusPill>
                    }
                    {brand?.state === 'failed' && brand.reason &&
                        <span className="rt-progress-reason">{brand.reason}</span>
                    }
                </li>
                <li className="rt-progress-step">
                    <span className="rt-progress-step-label">{__('Locations')}</span>
                    <StatusPill tone={counts.failed > 0 ? 'danger' : counts.pending > 0 ? 'info' : 'secondary'}>
                        {__(':done of :total provisioned', { ':done': counts.done, ':total': counts.total })}
                    </StatusPill>
                    {counts.failed > 0 &&
                        <span className="rt-progress-reason">{__(':count failed', { ':count': counts.failed })}</span>
                    }
                </li>
                <li className="rt-progress-step">
                    <span className="rt-progress-step-label">{__('Switch')}</span>
                    {switchStep({ switched, brand, readiness, waiting })}
                </li>
                <li className="rt-progress-step">
                    <span className="rt-progress-step-label">{__('Users')}</span>
                    {carriedOverStep(switched, carriedOver?.users, ':count With Access')}
                </li>
                <li className="rt-progress-step">
                    <span className="rt-progress-step-label">{__('Alerts')}</span>
                    {carriedOverStep(switched, carriedOver?.alerts, ':count Review Alerts')}
                </li>
            </ol>

            {tracked.length > 0 &&
                <ul className="rt-progress-locations">
                    {tracked.map(([businessId, location]) => {
                        const state = LOCATION_PROGRESS[location.state] || LOCATION_PROGRESS.pending

                        return (
                            <li key={businessId} className="rt-progress-location">
                                <span className="rt-progress-location-name">{names[businessId] || __('Location :id', { ':id': businessId })}</span>
                                <StatusPill tone={state.tone}>{__(state.label)}</StatusPill>
                                {location.reason && location.state !== 'enabled' &&
                                    <span className="rt-progress-reason">{location.reason}</span>
                                }
                            </li>
                        )
                    })}
                </ul>
            }

            {children}

            {entries.length > 0 &&
                <div className="rt-progress-log">
                    <h4 className="rt-progress-log-title">{__('Activity')}</h4>
                    <ul>
                        {entries.map(entry => {
                            const level = LEVELS[entry.level] || LEVELS.info

                            return (
                                <li key={entry.id} className={`rt-progress-entry rt-progress-entry-${level.tone}`}>
                                    <i className={`fas ${level.icon}`} aria-hidden="true" />
                                    <time dateTime={entry.at}>{formatTime(entry.at)}</time>
                                    {entry.business_id &&
                                        <strong>{names[entry.business_id] || __('Location :id', { ':id': entry.business_id })}</strong>
                                    }
                                    <span className="rt-progress-message">{entry.message}</span>
                                    {entry.attempt && entry.max_attempts && entry.attempt > 1 &&
                                        <span className="rt-progress-attempt">{__('attempt :n of :max', { ':n': entry.attempt, ':max': entry.max_attempts })}</span>
                                    }
                                </li>
                            )
                        })}
                    </ul>
                </div>
            }
        </div>
    )
}
